/* Mocked in-memory implementation of POD Client for local development and UI wiring. */

import { CancelToken } from 'axios';
import dayjs from 'dayjs';
import { IPodClient } from './IPodClient';
import {
  DetailedResponse,
  ItemConversion,
  PodConfigurationModel,
  PredictedDemandModel,
  StoreConfiguration,
  ApiError,
} from './podTypes';
import {
  mockConfigurations,
  mockStoreConfigurations,
  getMockPredictions,
  idGenerators,
} from './podMockData';

/* ---- Helper functions ---- */
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/* ---- POD Client (Mocked) ---- */
export class PodClientMocked implements IPodClient {
  /* ---- Configuration Methods ---- */

  async getConfigurations(
    configurationLevel: number,
    franchiseName?: string,
    storeNumber?: string,
    _cancelToken?: CancelToken,
  ): Promise<PodConfigurationModel[]> {
    await delay(200);

    return mockConfigurations.filter(config => {
      // Filter by configuration level
      if (config.configurationLevel !== configurationLevel) return false;

      // Filter by franchise if provided
      if (franchiseName && config.franchiseName !== franchiseName) return false;

      // Filter by store if provided
      if (storeNumber && config.storeNumber !== storeNumber) return false;

      return true;
    });
  }

  async getStoreConfiguration(
    franchiseName: string,
    storeNumber: string,
    _cancelToken?: CancelToken,
  ): Promise<StoreConfiguration> {
    await delay(100);

    const key = `${franchiseName}:${storeNumber}`;
    const config = mockStoreConfigurations.get(key);

    if (!config) {
      // Return default configuration if not found
      return { isPodEnabled: false };
    }

    return config;
  }

  async create(model: PodConfigurationModel, _cancelToken?: CancelToken): Promise<DetailedResponse> {
    await delay(300);

    // Check for duplicate
    const exists = mockConfigurations.some(
      c =>
        c.itemNumber === model.itemNumber &&
        c.storeNumber === model.storeNumber &&
        c.franchiseName === model.franchiseName,
    );

    if (exists) {
      throw new ApiError(409, 'Configuration already exists for this item/store combination');
    }

    // Create new configuration
    const newConfig: PodConfigurationModel = {
      ...model,
      id: idGenerators.nextConfigurationId(),
    };

    mockConfigurations.push(newConfig);

    return { status: 'success' };
  }

  async update(model: PodConfigurationModel, _cancelToken?: CancelToken): Promise<DetailedResponse> {
    await delay(300);

    const index = mockConfigurations.findIndex(c => c.id === model.id);

    if (index === -1) {
      throw new ApiError(404, 'Configuration not found');
    }

    mockConfigurations[index] = { ...model };

    return { status: 'success' };
  }

  async delete(id: number, _cancelToken?: CancelToken): Promise<DetailedResponse> {
    await delay(200);

    const index = mockConfigurations.findIndex(c => c.id === id);

    if (index === -1) {
      throw new ApiError(404, 'Configuration not found');
    }

    mockConfigurations.splice(index, 1);

    return { status: 'success' };
  }

  /* ---- Prediction Methods ---- */

  async getPredictions(
    franchiseName: string,
    storeNumber: string,
    availableDates: string[],
    _cancelToken?: CancelToken,
  ): Promise<PredictedDemandModel[]> {
    await delay(250);

    // Convert dates from DD-MM-YYYY to YYYY-MM-DD
    const formattedDates = availableDates.map(x => {
      const parsedDate = dayjs(x, 'DD-MM-YYYY');
      return parsedDate.format('YYYY-MM-DD');
    });

    return getMockPredictions(storeNumber, formattedDates);
  }

  async getPredictionsForItems(
    franchiseName: string,
    storeNumber: string,
    deliveryDates: string[],
    itemNumbersList: string[],
    _conversions?: ItemConversion[],
    _cancelToken?: CancelToken,
  ): Promise<PredictedDemandModel[]> {
    await delay(300);

    // Convert dates from DD-MM-YYYY to YYYY-MM-DD
    const formattedDates = deliveryDates.map(x => {
      const parsedDate = dayjs(x, 'DD-MM-YYYY');
      return parsedDate.format('YYYY-MM-DD');
    });

    return getMockPredictions(storeNumber, formattedDates, itemNumbersList);
  }
}
